import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Navigation } from "../components/Navigation";
import { api, eur, formatApiError } from "../lib/api";

export default function CartPage() {
  const { items, setQty, remove, subtotal, shipping, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    postal_code: "",
    city: "",
    country: "DE",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canCheckout =
    items.length > 0 &&
    form.name.trim() &&
    form.email.trim() &&
    form.address_line1.trim() &&
    form.postal_code.trim() &&
    form.city.trim();

  const doCheckout = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        customer: form,
        origin_url: window.location.origin,
      });
      // Save order number so success page can also fall back to it
      localStorage.setItem("last_order_number", data.order_number);
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="cart-page">
      <Navigation />

      <section className="pt-28 md:pt-36 pb-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <span className="gold-divider">Warenkorb</span>
          <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-8">
            Ihr <span className="italic gold-text">Warenkorb</span>
          </h1>

          {items.length === 0 ? (
            <div className="mt-16 text-center">
              <p
                className="text-[#A1A1AA] font-sans"
                data-testid="cart-empty"
              >
                Ihr Warenkorb ist leer.
              </p>
              <Link
                to="/shop"
                className="mt-6 inline-block btn-gold px-8 py-3 font-sans font-light tracking-widest text-xs uppercase"
              >
                Zum Shop
              </Link>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Line items */}
              <div className="lg:col-span-3 space-y-6" data-testid="cart-lines">
                {items.map((i) => (
                  <div
                    key={i.product_id}
                    data-testid={`cart-line-${i.product_id}`}
                    className="flex items-center gap-4 border-b border-[#D4AF37]/10 pb-6"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-serif italic text-lg text-[#E5E5E5]">
                        {i.title}
                      </p>
                      {i.subtitle && (
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-sans mt-1">
                          {i.subtitle}
                        </p>
                      )}
                      <p className="mt-2 text-sm text-[#A1A1AA] font-sans">
                        {eur(i.price_eur)} × {i.quantity} · Versand{" "}
                        {eur(Number(i.shipping_eur || 0) * i.quantity)}
                      </p>
                    </div>

                    <div className="inline-flex items-center border border-[#D4AF37]/20">
                      <button
                        onClick={() => setQty(i.product_id, i.quantity - 1)}
                        className="px-3 py-2 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid={`cart-qty-minus-${i.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-sm font-serif text-[#E5E5E5]">
                        {i.quantity}
                      </span>
                      <button
                        onClick={() => setQty(i.product_id, i.quantity + 1)}
                        className="px-3 py-2 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid={`cart-qty-plus-${i.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <p className="hidden sm:block w-24 text-right font-serif text-[#D4AF37]">
                      {eur(Number(i.price_eur) * i.quantity)}
                    </p>

                    <button
                      onClick={() => remove(i.product_id)}
                      className="text-[#A1A1AA] hover:text-red-400 transition-colors"
                      aria-label="Entfernen"
                      data-testid={`cart-remove-${i.product_id}`}
                    >
                      <Trash2 size={16} strokeWidth={1.4} />
                    </button>
                  </div>
                ))}

                <button
                  onClick={clear}
                  className="text-[11px] uppercase tracking-[0.35em] font-sans font-light text-[#A1A1AA] hover:text-red-400 transition-colors"
                  data-testid="cart-clear"
                >
                  Warenkorb leeren
                </button>
              </div>

              {/* Checkout form + summary */}
              <div className="lg:col-span-2">
                <div className="border border-[#D4AF37]/15 p-6 md:p-8 bg-[#0b0b0b] space-y-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]/70 font-sans">
                      Lieferadresse
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-3">
                      <Input label="Name" testid="co-name" value={form.name} onChange={set("name")} />
                      <Input label="E-Mail" testid="co-email" type="email" value={form.email} onChange={set("email")} />
                      <Input label="Telefon (optional)" testid="co-phone" value={form.phone} onChange={set("phone")} />
                      <Input label="Straße & Nr." testid="co-street" value={form.address_line1} onChange={set("address_line1")} />
                      <Input label="Zusatz (optional)" testid="co-street2" value={form.address_line2} onChange={set("address_line2")} />
                      <div className="grid grid-cols-3 gap-3">
                        <Input label="PLZ" testid="co-zip" value={form.postal_code} onChange={set("postal_code")} />
                        <div className="col-span-2">
                          <Input label="Ort" testid="co-city" value={form.city} onChange={set("city")} />
                        </div>
                      </div>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
                          Land
                        </span>
                        <select
                          value={form.country}
                          onChange={set("country")}
                          data-testid="co-country"
                          className="mt-1 w-full bg-transparent border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60"
                        >
                          <option value="DE">Deutschland</option>
                          <option value="AT">Österreich</option>
                          <option value="CH">Schweiz</option>
                          <option value="FR">Frankreich</option>
                          <option value="NL">Niederlande</option>
                          <option value="BE">Belgien</option>
                          <option value="IT">Italien</option>
                          <option value="ES">Spanien</option>
                          <option value="DK">Dänemark</option>
                          <option value="PL">Polen</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="border-t border-[#D4AF37]/10 pt-4 space-y-2 text-sm">
                    <Row label="Zwischensumme" value={eur(subtotal)} />
                    <Row label="Versand" value={eur(shipping)} />
                    <Row label="Gesamt" value={eur(total)} bold />
                  </div>

                  {error && (
                    <p
                      className="text-sm text-red-400 font-sans"
                      data-testid="cart-error"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    onClick={doCheckout}
                    disabled={!canCheckout || submitting}
                    data-testid="cart-checkout"
                    className="btn-gold w-full inline-flex items-center justify-center gap-3 px-6 py-4 font-sans font-light tracking-widest text-xs uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Weiterleitung…" : (
                      <>
                        Zur Kasse
                        <ArrowRight size={14} strokeWidth={1.4} />
                      </>
                    )}
                  </button>

                  <p className="text-[10px] text-[#A1A1AA] font-sans leading-relaxed">
                    Sichere Zahlung über Stripe (Kreditkarte, PayPal, Klarna,
                    SEPA, Apple Pay & Google Pay — je nach Verfügbarkeit im
                    Stripe-Konto).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const Input = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
      {label}
    </span>
    <input
      {...rest}
      data-testid={testid}
      className="mt-1 w-full bg-transparent border border-[#D4AF37]/20 px-3 py-2 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60 placeholder:text-[#A1A1AA]/40"
    />
  </label>
);

const Row = ({ label, value, bold }) => (
  <div
    className={`flex items-center justify-between ${
      bold ? "font-serif text-lg text-[#D4AF37]" : "text-[#E5E5E5]/80 font-sans"
    }`}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
